import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useEffect } from 'react';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import { usePublish } from '@atlaskit/rovo-triggers';
import { convertProsemirrorNodeToMarkdown } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';
import { getMentionMap } from '@atlassian/ai-model-io/utils/mention-map';
import { getPluginState } from '../../../pm-plugins/decoration/decoration-plugin-factory';
import { updatePublish } from '../../../pm-plugins/rovo-agents/commands';
import { getAIHighlightPositions } from '../../utils/selection';
export var getSelectionContent = function getSelectionContent(_ref) {
  var modalDecorationSet = _ref.modalDecorationSet,
    doc = _ref.doc;
  var positions = getAIHighlightPositions({
    modalDecorationSet: modalDecorationSet
  });
  return positions ? doc.slice(positions[0], positions[1], true).content : undefined;
};
/**
 *  This component is required so we can call the usePublish hook
 *  and store the publish function in the rovo-agents plugin on mount.
 */
export var PublishToRovo = function PublishToRovo(_ref2) {
  var editorView = _ref2.editorView,
    getMentionNameDetails = _ref2.getMentionNameDetails,
    editorPluginAIProvider = _ref2.editorPluginAIProvider,
    editorApi = _ref2.editorApi;
  var publish = usePublish('ai-mate');
  var product = editorPluginAIProvider.product;
  var publishDocAndSelection = React.useCallback( /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref3) {
      var doc, selection, queueTask, _getPluginState, modalDecorationSet, selectionContent, selectionContentFromDecorations, mentionMap, _convertProsemirrorNo, selectionMD;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            doc = _ref3.doc, selection = _ref3.selection;
            // This is used as performance optimization since the publish function is called very often
            // and we don't want to block the main thread for this operation.
            // requestIdleCallback is not supported by all browsers, so we fallback to requestAnimationFrame
            queueTask = window.requestIdleCallback || window.requestAnimationFrame;
            if (!(!doc || !selection)) {
              _context.next = 5;
              break;
            }
            queueTask(function () {
              publish({
                type: 'editor-context-payload',
                source: 'editor',
                product: product,
                data: undefined
              });
            });
            return _context.abrupt("return");
          case 5:
            // Retrieve the modal decoration set from the plugin state to access the selection state,
            // since the editor's selection is cleared when the modal opens.
            _getPluginState = getPluginState(editorView.state), modalDecorationSet = _getPluginState.modalDecorationSet; // Initially, assume the selected content from editorView will be used
            selectionContent = selection.content().content;
            selectionContentFromDecorations = getSelectionContent({
              modalDecorationSet: modalDecorationSet,
              doc: doc
            });
            if (selectionContentFromDecorations) {
              selectionContent = selectionContentFromDecorations;
            }
            mentionMap = {};
            if (!getMentionNameDetails) {
              _context.next = 14;
              break;
            }
            _context.next = 13;
            return getMentionMap({
              node: doc,
              getMentionNameDetails: getMentionNameDetails
            });
          case 13:
            mentionMap = _context.sent;
          case 14:
            _convertProsemirrorNo = convertProsemirrorNodeToMarkdown({
              node: Fragment.from(selectionContent),
              featureToggles: {
                markdownPlus: false
              },
              mentionMap: mentionMap
            }), selectionMD = _convertProsemirrorNo.markdown;
            queueTask(function () {
              publish({
                type: 'editor-context-payload',
                source: 'editor',
                product: product,
                data: {
                  document: {
                    type: 'text/adf',
                    content: doc.toJSON()
                  },
                  selection: {
                    type: 'text/markdown',
                    content: selectionMD
                  }
                }
              });
            });
          case 16:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function (_x) {
      return _ref4.apply(this, arguments);
    };
  }(), [editorView, getMentionNameDetails, product, publish]);
  usePluginStateEffect(editorApi, ['focus'], function (_ref5) {
    var focusState = _ref5.focusState;
    // this is the same as onBlur
    if (!(focusState !== null && focusState !== void 0 && focusState.hasFocus)) {
      publishDocAndSelection({
        doc: editorView.state.doc,
        selection: editorView.state.selection
      });
    }
  });
  usePluginStateEffect(editorApi, ['aiExperience'], function (_ref6) {
    var _aiExperienceState$no;
    var aiExperienceState = _ref6.aiExperienceState;
    // When text is selected or deselected, publish the updated selection context to Rovo
    if (((_aiExperienceState$no = aiExperienceState === null || aiExperienceState === void 0 ? void 0 : aiExperienceState.nonCursorSelectionChangeCount) !== null && _aiExperienceState$no !== void 0 ? _aiExperienceState$no : 0) >= 1) {
      publishDocAndSelection({
        doc: editorView.state.doc,
        selection: editorView.state.selection
      });
    }
  });
  useEffect(function () {
    return function () {
      publish({
        type: 'editor-context-payload',
        source: 'editor',
        product: product,
        data: undefined
      });
    };
  }, [publish, product]);
  updatePublish(publish)(editorView.state, editorView.dispatch);
  return /*#__PURE__*/React.createElement(React.Fragment, null);
};